import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Form } from '@unform/web';
import moment from 'moment';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { Spinner } from '~/components';
import { Input, Autocomplete, DateTime } from '~/components/Form';
import { t, validCharacters } from '~/helpers';
import {
  storeOperacaoRecebimento,
  updateOperacaoRecebimento,
} from '~/store/modules/operacaorecebimento/actions';

export default function CreateOrUpdate({ reference, data, create }) {
  const dispatch = useDispatch();

  const { client } = useSelector((state) => state.user.profile);
  const { loading, find, params } = useSelector(
    (state) => state.operacaorecebimento
  );

  const initials = create ? data : { ...find, ...data };

  async function handleSubmit(param) {
    try {
      const schema = Yup.object().shape({
        idposto: create
          ? Yup.string()
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .required(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string().nullable(),
        idproduto: create
          ? Yup.string()
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .required(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string().nullable(),
        datahoradescarga: create
          ? Yup.string()
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .required(t('s.geral.validacao.campoobrigatorio'))
          : Yup.string().nullable(),
        numeronfdescarga: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
        volumenfdescarga: Yup.number()
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .required(t('s.geral.validacao.campoobrigatorio'))
          .integer(t('s.geral.validacao.numerointeiro')),
        placactdescarga: Yup.string()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .typeError(t('s.geral.validacao.campoobrigatorio'))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            (value) => validCharacters(value)
          ),
      });

      await schema.validate(param, {
        abortEarly: false,
      });

      const objSend = {
        ...initials,
        ...param,
        idcliente: client,
      };

      if (create) {
        await dispatch(storeOperacaoRecebimento(objSend, params));
      } else {
        await dispatch(updateOperacaoRecebimento(objSend, params));
      }
    } catch (err) {
      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });

        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (!loading) {
      reference.current.setData(initials);
    }
  }, [loading, initials]);

  return (
    <>
      {loading ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
          <Autocomplete
            name="idposto"
            label="nomeposto"
            // url="postos"
            url="postoscomboios"
            unique="idposto"
            title={t('s.menu.configuracao.posto')}
            disabled={!create}
          />

          <Autocomplete
            name="idproduto"
            label="nomeproduto"
            url="produtos"
            unique="idproduto"
            title={t('s.menu.configuracao.produto')}
            disabled={!create}
          />

          <DateTime
            time
            name="datahoradescarga"
            label={t('s.operacao.recebimento.datahoradescarga')}
            disabled={!create}
            serialize={(value) =>
              value.set({ seconds: 0 }).format('DD/MM/YYYY HH:mm:ss')
            }
          />

          <Input
            name="numeronfdescarga"
            label={t('s.operacao.recebimento.numeronfdescarga')}
          />

          <Input
            name="volumenfdescarga"
            label={t('s.operacao.recebimento.volumenfdescarga')}
            type="number"
          />

          <Input
            name="placactdescarga"
            label={t('s.operacao.recebimento.placactdescarga')}
          />
        </Form>
      )}
    </>
  );
}

CreateOrUpdate.propTypes = {
  reference: PropTypes.objectOf(PropTypes.any).isRequired,
  create: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(PropTypes.any),
};

CreateOrUpdate.defaultProps = {
  data: {},
};
